import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Accordion } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import userImg from "../../assets/images/User.png";

import TopHeadding from "../../components/layout/TopHeadding";
import HistoryTable from "../../components/tables/HistoryTable";
import FilterTopBar from "../../components/navigation/FilterTopBar";

import calendar_icon from "../../assets/images/calendar_icon.svg";
import { useAppSelector } from "../../hooks/useAppSelector";
import { formatDateForApi, formatDateForLabel } from "../../utils/dateUtils";
import {
  exportPayrollHistoryCSV,
  fetchBatchHistory,
  fetchEmployeeBatchHistory,
} from "../../store/slices/historySlice";

const getCurrentMonthRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return [start, end];
};

const History = () => {
  const dispatch = useDispatch();
  const { user, role, isAuthenticated } = useAppSelector((s) => s.user);

  const { listDataByKey, listLoadingByKey, entityErrorByKey } = useSelector(
    (state) => state.history || {},
  );

  const historyData = listDataByKey?.["history"] || [];
  const loading = listLoadingByKey["history"] || false;
  const error = entityErrorByKey["history"];

  const [start, end] = getCurrentMonthRange();
  const [dateRange, setDateRange] = useState({
    startDate: start,
    endDate: end,
  });

  const [search, setSearch] = useState("");
  const { startDate, endDate } = dateRange;

  const BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL || "";

  useEffect(() => {
    if (!startDate || !endDate || !role || !isAuthenticated) return;

    const params = {
      pageIndex: 0,
      pageSize: 2147483647,
      startDate: formatDateForApi(startDate),
      endDate: formatDateForApi(endDate),
    };

    if (search) params.search = search;

    if (role === "SuperAdmin") {
      dispatch(fetchBatchHistory({ params, key: "history" }));
    } else {
      dispatch(
        fetchEmployeeBatchHistory({
          params: { ...params, clientId: user?.id },
          key: "history",
        }),
      );
    }
  }, [dispatch, startDate, endDate, search, role, user?.id, isAuthenticated]);

  const handleExportCSV = () => {
    dispatch(
      exportPayrollHistoryCSV(
        role === "SuperAdmin"
          ? {
              startDate: formatDateForApi(startDate),
              endDate: formatDateForApi(endDate),
              search,
            }
          : {
              startDate: formatDateForApi(startDate),
              endDate: formatDateForApi(endDate),
              search,
              clientId: user?.id,
            },
      ),
    );
  };

  return (
    <div className="Companies">
      <TopHeadding title="Payroll History" showDatepicker={false} />

      <div className="container-fluid mt_20">
        <div className="d-flex justify-content-between align-items-center top_headding">
          <div className="datepicker position-relative">
            <DatePicker
              selectsRange
              startDate={startDate}
              endDate={endDate}
              onChange={([start, end]) =>
                setDateRange({ startDate: start, endDate: end })
              }
              dateFormat="MM/dd/yyyy"
              className="form-control"
            />
            <img
              src={calendar_icon}
              className="datepicker_icon"
              alt="calendar"
            />
          </div>

          <FilterTopBar
            title="Payroll"
            search={search}
            onSearchChange={setSearch}
            placeholder={
              role === "SuperAdmin" ? "Search Client" : "Search Company"
            }
            showExport
            showFilter={false}
            customExportHandler={handleExportCSV}
            disableExport={historyData.length === 0}
          />
        </div>
      </div>

      <div className="accordain mt_30 container-fluid">
        <div className="row">
          <div className="col-md-12">
            <div className="custom_accordion">
              {error ? (
                <div className="text-center p-4">
                  Error loading history: {error}
                </div>
              ) : loading ? (
                <div className="text-center p-4">Loading</div>
              ) : historyData.length === 0 ? (
                <div className="text-center p-4">No History Found</div>
              ) : (
                <Accordion>
                  {historyData.map((item, index) => {
                    // ✅ SUPERADMIN: flatten companies into ONE table
                    const superAdminRows =
                      role === "SuperAdmin"
                        ? item.batchHistories?.flatMap((batch) => {
                            return batch.companies.map((company) => ({
                              companyId: company.companyId,
                              id: `${batch.payrollHeaderId}_${company.companyId}_${company.paymentMethod}`,
                              name: company.companyName,
                              email: company.email,
                              designation: "",
                              revenue: `$${company.amount.toLocaleString()}`,
                              payment: company.paymentMethod,
                              image: company.profile
                                ? `${BASE_URL}${company.profile}`
                                : userImg,
                              link: `/companydetailpage/${company.companyId}`,
                              payrollFrom: batch.payrollFrom,
                              payrollTo: batch.payrollTo,
                              employeeCount: company.employeeCount,
                            }));
                          }) || []
                        : [];

                    return (
                      <Accordion.Item eventKey={index.toString()} key={index}>
                        <Accordion.Header>
                          <div className="d-flex justify-content-between align-items-center w-100 flex-wrap">
                            <div className="col-12 col-md-3 text-center mb-1 mb-md-0">
                              <div className="d-flex align-items-center justify-content-start">
                                <div className="company_img">
                                  <img
                                    src={
                                      item.profile === null
                                        ? userImg
                                        : BASE_URL + item.profile
                                    }
                                    alt="profile"
                                  />
                                </div>
                                <h4
                                  className="mb-0 mr-5"
                                  title={
                                    role === "SuperAdmin"
                                      ? item.clientName
                                      : item.companyName
                                  }
                                >
                                  {role === "SuperAdmin"
                                    ? item.clientName
                                    : item.companyName}
                                </h4>
                              </div>
                            </div>
                            <div className="col-12 col-md-3 text-center mb-1 mb-md-0">
                              <h5 className="mb-0">
                                <span>Date paid</span> –{" "}
                                {formatDateForLabel(item.datePaid)}
                              </h5>
                            </div>
                            <div className="col-12 col-md-3 text-center mb-1 mb-md-0">
                              <h5 className="mb-0">
                                {role === "SuperAdmin" ? (
                                  <>
                                    <span>Company</span> – {item.companyCount}
                                  </>
                                ) : (
                                  <>
                                    <span>Employee</span> – {item.employeeCount}
                                  </>
                                )}
                              </h5>
                            </div>
                            <div className="col-12 col-md-3 text-center mb-1 mb-md-0">
                              <h5 className="mb-0">
                                <span>Total Amount</span> – $
                                {item.totalAmount.toLocaleString()}
                              </h5>
                            </div>
                          </div>
                        </Accordion.Header>

                        <Accordion.Body className="pl_20">
                          {role === "SuperAdmin" ? (
                            <HistoryTable
                              role={role}
                              type="payroll"
                              rows={superAdminRows}
                            />
                          ) : (
                            <HistoryTable
                              role={role}
                              type="employee"
                              rows={item.employees?.map((emp) => ({
                                payrollFrom: item.payrollFrom,
                                payrollTo: item.payrollTo,
                                id: emp.payrollEmployeeId,
                                employeeId: emp.employeeId,
                                name: emp.employeeName,
                                email: emp.email,
                                designation: emp.designation,
                                revenue: `$${emp.amount.toLocaleString()}`,
                                payment: emp.paymentMode,
                                image: emp.profile
                                  ? `${BASE_URL}${emp.profile}`
                                  : userImg,
                                link: `/employeedetail/${emp.payrollEmployeeId}`,
                              }))}
                            />
                          )}
                        </Accordion.Body>
                      </Accordion.Item>
                    );
                  })}
                </Accordion>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default History;
